import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useUser } from '@/features/auth/hooks/use-user';
import { useUserOrgs } from '@/features/org/hooks/use-user-orgs';
import { AcceptInvitationLayout } from '@/features/partners/components/layouts/accept-partner-invitation-layout';
import { AcceptInvitationBudgetPage } from '@/features/partners/components/pages/accept-partner-invitation-budget-page';
import { AcceptInvitationCategoryPage } from '@/features/partners/components/pages/accept-partner-invitation-category-page';
import { AcceptInvitationInfoPage } from '@/features/partners/components/pages/accept-partner-invitation-info-page';
import { AcceptInvitationInviteMembersInputPage } from '@/features/partners/components/pages/accept-partner-invitation-invite-members-input-page';
import { AcceptInvitationInviteMembersOverviewPage } from '@/features/partners/components/pages/accept-partner-invitation-invite-members-overview-page';
import { AcceptInvitationSubcategoryPage } from '@/features/partners/components/pages/accept-partner-invitation-subcategory-page';
import { useAcceptSubprojectInvitationStore } from '@/features/partners/store/use-accept-subproject-invitation-store';
import { ErrorCard } from '@/shared/components/card-elements/error-card';
import { LoadingCard } from '@/shared/components/card-elements/loading-card';
import {
  FlowLayout,
  FlowLayoutBody,
} from '@/shared/components/flow/flow-layout';
import { Background } from '@/shared/components/layouts/background';
import { Project } from '@/shared/types/api';

const Layout = ({ children }: { children: React.ReactNode }) => (
  <Background>
    <FlowLayout>
      <FlowLayoutBody>{children}</FlowLayoutBody>
    </FlowLayout>
  </Background>
);

export type InvitationStatus = 'accepted' | 'declined' | 'undecided';

export type Props = {
  invitationToken: string | null;
  isInvitationLoading: boolean;
  status: InvitationStatus | null | undefined;
  project: Project | null | undefined;
  toEmail: string | null | undefined;
  kpisIds: number[] | null | undefined;
};

export const AcceptSubprojectInvitationFlow = ({
  invitationToken,
  isInvitationLoading,
  status,
  project,
  toEmail,
  kpisIds,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('partnerInvitation');

  const { data: user, isLoading: isUserLoading } = useUser();
  const {
    data: orgs,
    isLoading: isOrgsLoading,
    isFetching: isOrgsFetching,
    isError: isOrgsError,
  } = useUserOrgs();

  const nonSelfOrgs = useMemo(
    () => orgs?.filter((org) => !org.self_orga_user_id) ?? [],
    [orgs],
  );

  const { data: acceptData, reset } = useAcceptSubprojectInvitationStore();

  useEffect(() => {
    reset();
  }, [reset]);

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user && !isUserLoading && invitationToken) {
      const redirectPath =
        paths.partners.acceptInvitation.getHref(invitationToken);
      navigate(paths.auth.login.getHref(redirectPath));
    }
  }, [user, isUserLoading, invitationToken, navigate]);

  // Redirect to org creation if user has no real (non-self) org
  // Wait for orgs to be fully loaded (not fetching) to avoid acting on stale cache
  const needsOrgCreation =
    !isOrgsLoading &&
    !isOrgsFetching &&
    user &&
    orgs &&
    nonSelfOrgs.length === 0;

  useEffect(() => {
    if (needsOrgCreation) {
      const currentPath = location.pathname + location.search;
      navigate(paths.org.create.getHref(currentPath), { replace: true });
    }
  }, [needsOrgCreation, navigate, location]);

  // Redirect if invitation already processed
  useEffect(() => {
    const isProcessed = status === 'accepted' || status === 'declined';
    if (isProcessed) {
      navigate(paths.home.getHref());
    }
  }, [status, navigate]);

  // --- Early returns after all hooks ---

  if (!invitationToken) {
    return (
      <Layout>
        <ErrorCard
          title={t('errorTitle')}
          message={t('missingToken')}
          buttonText={t('goHome')}
          onButtonClick={() => navigate(paths.home.getHref())}
        />
      </Layout>
    );
  }

  if (isOrgsError) {
    return (
      <Layout>
        <ErrorCard
          title={t('cantFindOrgsTitle')}
          message={t('cantFindOrgsMessage')}
        />
      </Layout>
    );
  }

  // Show loading while data is being fetched (including refetches after org creation)
  if (isUserLoading || isInvitationLoading || isOrgsLoading || isOrgsFetching) {
    return (
      <Layout>
        <LoadingCard title={t('loadingTitle')} />
      </Layout>
    );
  }

  // Show loading while redirect to org creation is pending
  if (needsOrgCreation) {
    return (
      <Layout>
        <LoadingCard title={t('loadingTitle')} />
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <ErrorCard
          title={t('errorTitle')}
          message={t('fetchError')}
          buttonText={t('goHome')}
          onButtonClick={() => navigate(paths.home.getHref())}
        />
      </Layout>
    );
  }

  // Check if the logged-in user is the intended recipient
  if (user?.email !== toEmail) {
    return (
      <Layout>
        <ErrorCard
          title={t('errorTitle')}
          message={t('wrongUser')}
          buttonText={t('goHome')}
          onButtonClick={() => navigate(paths.home.getHref())}
        />
      </Layout>
    );
  }

  const step = acceptData.step;

  return (
    <AcceptInvitationLayout currentStep={step}>
      {step === 'info' && (
        <AcceptInvitationInfoPage
          clientSubproject={project}
          orgs={nonSelfOrgs}
        />
      )}
      {step === 'category' && <AcceptInvitationCategoryPage />}
      {step === 'subcategory' && <AcceptInvitationSubcategoryPage />}
      {step === 'budgets' && (
        <AcceptInvitationBudgetPage
          clientSubproject={project}
          kpisIds={kpisIds || []}
        />
      )}
      {step === 'inviteMembersInput' && (
        <AcceptInvitationInviteMembersInputPage />
      )}
      {step === 'inviteMembersOverview' && (
        <AcceptInvitationInviteMembersOverviewPage
          invitationToken={invitationToken}
        />
      )}
    </AcceptInvitationLayout>
  );
};
